import {
  suggestOrgSlug,
  type CreateTeamRequest,
  type UpdateTeamRequest,
} from '@gb-ticket/shared-types';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const NONE = '__none__';

export type TeamFormValues = {
  id?: string;
  name: string;
  slug: string;
  departmentId: string | null;
  leadId: string | null;
  isActive: boolean;
};

type DepartmentOption = { id: string; name: string; isActive: boolean };
type LeadOption = { id: string; displayName: string; email?: string };

type TeamFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: TeamFormValues | null;
  busy: boolean;
  departments: DepartmentOption[];
  leads: LeadOption[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: { create?: CreateTeamRequest; update?: UpdateTeamRequest }) => Promise<void>;
};

export function TeamFormDialog({
  open,
  mode,
  initial,
  busy,
  departments,
  leads,
  onOpenChange,
  onSubmit,
}: TeamFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {open ? (
          <TeamFormFields
            key={`${mode}:${initial?.id ?? 'new'}`}
            mode={mode}
            initial={initial}
            busy={busy}
            departments={departments}
            leads={leads}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TeamFormFields({
  mode,
  initial,
  busy,
  departments,
  leads,
  onOpenChange,
  onSubmit,
}: Omit<TeamFormDialogProps, 'open'>) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [departmentId, setDepartmentId] = useState(initial?.departmentId ?? NONE);
  const [leadId, setLeadId] = useState(initial?.leadId ?? NONE);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const departmentOptions = useMemo(() => {
    const active = departments.filter((item) => item.isActive);
    const current = departments.find((item) => item.id === initial?.departmentId);
    if (current && !current.isActive && !active.some((item) => item.id === current.id)) {
      return [current, ...active];
    }
    return active;
  }, [departments, initial]);

  const leadOptions = useMemo(() => {
    const byId = new Map(leads.map((lead) => [lead.id, lead]));
    if (initial?.leadId && !byId.has(initial.leadId)) {
      byId.set(initial.leadId, {
        id: initial.leadId,
        displayName: 'Aktuelle Leitung',
      });
    }
    return [...byId.values()];
  }, [leads, initial]);

  const selectedLeadLabel = useMemo(() => {
    if (leadId === NONE) {
      return undefined;
    }
    return leadOptions.find((lead) => lead.id === leadId)?.displayName;
  }, [leadId, leadOptions]);

  const canSubmit = name.trim().length >= 2 && slug.trim().length >= 2;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{mode === 'create' ? 'Team hinzufügen' : 'Team bearbeiten'}</DialogTitle>
        <DialogDescription>
          Support-Teams für Warteschlangen und Benachrichtigungen.
        </DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="team-name">Name</Label>
          <Input
            id="team-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugTouched) {
                setSlug(suggestOrgSlug(next));
              }
            }}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="team-slug">Kurzname (Slug)</Label>
          <Input
            id="team-slug"
            value={slug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value.toLowerCase());
            }}
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Nur Kleinbuchstaben, Ziffern und Bindestriche.
          </p>
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="team-department">Abteilung</Label>
          <Select value={departmentId} onValueChange={setDepartmentId}>
            <SelectTrigger id="team-department" className="w-full">
              <SelectValue placeholder="Keine Abteilung" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine Abteilung</SelectItem>
              {departmentOptions.map((department) => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                  {!department.isActive ? ' (inaktiv)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="team-lead">Teamleitung</Label>
          <Select value={leadId} onValueChange={setLeadId}>
            <SelectTrigger id="team-lead" className="w-full">
              <SelectValue placeholder="Keine Leitung">{selectedLeadLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine Leitung</SelectItem>
              {leadOptions.map((lead) => (
                <SelectItem
                  key={lead.id}
                  value={lead.id}
                  textValue={lead.displayName}
                  title={lead.email}
                >
                  <span className="flex min-w-0 flex-col items-start gap-0.5 overflow-hidden">
                    <span className="truncate">{lead.displayName}</span>
                    {lead.email ? (
                      <span className="truncate text-xs text-muted-foreground">{lead.email}</span>
                    ) : null}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {mode === 'edit' && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <div className="min-w-0">
              <Label htmlFor="team-active">Aktiv</Label>
              <p className="text-xs text-muted-foreground">
                Inaktive Teams erscheinen nicht in neuen Zuweisungen.
              </p>
            </div>
            <Switch id="team-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={busy || !canSubmit}
          onClick={() => {
            const department = departmentId === NONE ? null : departmentId;
            const lead = leadId === NONE ? null : leadId;
            if (mode === 'create') {
              void onSubmit({
                create: {
                  name: name.trim(),
                  slug: slug.trim(),
                  departmentId: department,
                  leadId: lead,
                },
              });
              return;
            }
            void onSubmit({
              update: {
                name: name.trim(),
                slug: slug.trim(),
                departmentId: department,
                leadId: lead,
                isActive,
              },
            });
          }}
        >
          {mode === 'create' ? 'Anlegen' : 'Speichern'}
        </Button>
      </DialogFooter>
    </>
  );
}
